import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import { searchStudents } from "../../../../services/studentService";
import { LinkedStudent } from "../../../../types/guardianTypes";
import CommonMultiSelect from "../../../Common/CommonMultiSelect";

interface Props {
  value: string[];
  onChange: (value: string[]) => void;
  initialStudents?: LinkedStudent[];
}

const SelectLinkedStudents: React.FC<Props> = ({ value, onChange, initialStudents = [] }) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
  const [fetching, setFetching] = useState(false);

  // ✅ Memoize initial students mapping
  const mappedInitialOptions = useMemo(() => {
    return initialStudents.map((student) => ({
      label: `${student.user.firstName} ${student.user.lastName}`,
      value: student.id,
    }));
  }, [initialStudents]);

  // ✅ Add mapped initial students to options only once
  useEffect(() => {
    setOptions((prev) => {
      const merged = [...prev, ...mappedInitialOptions];
      const unique = new Map<string, { label: string; value: string }>();
      merged.forEach((item) => unique.set(item.value, item));
      return Array.from(unique.values());
    });
  }, [mappedInitialOptions]);

  // ✅ Search logic
  const fetchStudentOptions = debounce(async (search: string) => {
    setFetching(true);
    try {
      const response = await searchStudents(search);
      const fetched = response.data.map((student: any) => ({
        label: `${student.firstName} ${student.lastName}`,
        value: student.id,
      }));
      setOptions((prev) => {
        const merged = [...prev, ...fetched];
        const unique = new Map<string, { label: string; value: string }>();
        merged.forEach((item) => unique.set(item.value, item));
        return Array.from(unique.values());
      });
    } catch (err) {
      console.error("Search failed", err);
    } finally {
      setFetching(false);
    }
  }, 300);

  return (
    <CommonMultiSelect
      value={value}
      onChange={onChange}
      onSearch={fetchStudentOptions}
      options={options}
      loading={fetching}
      placeholder={t("select_students")}
    />
  );
};

export default SelectLinkedStudents;
